<!-- Copied and adjusted from https://bitbucket.org/home_search/homesearch-vue-property/src/master/src/components/location/autocomplete/LocationAutocomplete.vue -->
<!-- Adjustments: made abstract (any API response structure), removed fuzzy search -->
<!-- Consider moving to homesearch-vue-common package -->
<template>
  <VAutocomplete
    v-model="searchValue"
    :search-results="autocompleteData"
    :search-result-field="searchResultField"
    :loading="isLoading"
    :label="label"
    :placeholder="placeholder"
    :class="variantClassName"
    :invalid="invalid"
    :item-validator="itemValidator"
    :trigger-focus="triggerFocus"
    :disabled="disabled"
    :lock-body-scroll="lockBodyScroll"
    :modal-height-adjuster="modalHeightAdjuster"
    @update:model-value="handleAutocompleteModelValueUpdate"
    @on-select="onSelect"
    @on-focus="emitOnFocus"
    @on-blur="emitOnBlur"
  >
    <template v-if="hasSearchIcon" #prepend>
      <VInputIconBox>
        <img :src="searchIcon" alt="Search" />
      </VInputIconBox>
    </template>
    <template v-if="$slots.append" #append>
      <slot name="append" />
    </template>
    <template v-if="$slots.noResults" #no-results>
      <slot v-if="!hasAutocompleteData" name="noResults" />
    </template>
  </VAutocomplete>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';

  // Types
  import type { PropType } from 'vue';
  import type { TSearchPerformer } from '~/misc/search/types';
  import type { TApiHttpClientErrorResponse, TApiHttpClientError } from '~/misc/resource/types';
  import type { IAutocompleteModalHeightAdjuster } from '~/misc/v-autocomplete/types';

  // Components
  import VAutocomplete from '~/misc/v-autocomplete';
  import VInputIconBox from '~/misc/v-input-icon-box';

  // Utils
  import { debounce } from 'lodash-es';

  // Assets
  import searchIcon from '~/assets/images/icons/search.svg';

  export default defineComponent({
    name: 'SearchAutocomplete',
    components: {
      VAutocomplete,
      VInputIconBox,
    },
    props: {
      modelValue: {
        type: String,
        default: '',
      },
      value: {
        type: String,
        default: '',
      },
      label: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      triggerFocus: {
        type: Boolean,
        default: false,
      },
      invalid: {
        type: [Array, Boolean],
        default: false,
      },
      itemValidator: {
        type: Function as PropType<(option: unknown) => boolean>,
        required: false,
      },
      variantClassName: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      searchResultField: {
        type: String,
        required: true,
      },
      /**
       * Debounce timeout for typeahead fields
       *
       * The average person types at approx. 40 words per minute, this equates to
       * roughly 190 characters per minute, or a maximum interval between key presses
       * of 300ms.
       */
      typeaheadDebounceTimeoutInMS: {
        type: Number,
        default: 300,
      },
      searchMinChars: {
        type: Number,
        default: 1,
      },
      lockBodyScroll: {
        type: Boolean,
        default: false,
      },
      modalHeightAdjuster: {
        type: Object as PropType<IAutocompleteModalHeightAdjuster | null>,
        default: undefined,
      },
      searchPerformer: {
        type: Object as PropType<TSearchPerformer>,
        required: true,
      },
      hasSearchIcon: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['on-select-item', 'update:modelValue', 'on-error', 'on-focus', 'on-blur'],
    setup() {
      return { searchIcon };
    },
    data: (): {
      autocompleteData: unknown[];
      isLoading: boolean;
      searchValue: string;
    } => ({
      autocompleteData: [],
      isLoading: false,
      searchValue: '',
    }),
    computed: {
      debouncedSearch(): (query: string) => void {
        return debounce(async (query: string) => {
          if (!query) {
            this.setIsLoading(false);

            return;
          }

          await this.search(query);
        }, this.typeaheadDebounceTimeoutInMS);
      },
      hasAutocompleteData(): boolean {
        return this.autocompleteData.length > 0;
      },
    },
    watch: {
      modelValue: {
        immediate: true,
        handler(modelValue: string) {
          this.setSearchValue(modelValue);
        },
      },
      value(value: string) {
        if (!value) {
          return;
        }

        this.setSearchValue(value);
        this.debouncedSearch(value);
      },
    },
    methods: {
      handleAutocompleteModelValueUpdate(value: string) {
        this.emitUpdateModelValue(value);
        this.debouncedSearch(value);
      },
      /**
       * Perform a typeahead search instantly.
       *
       * @param query  Search term/query
       */
      async search(query: string) {
        if (!this.isSearchEnabled(query)) {
          return;
        }

        try {
          this.setIsLoading(true);
          this.clearAutocompleteData();

          const searchResult: unknown[] = await this.searchPerformer.searchByQuery(query);

          this.setAutocompleteData(searchResult);
        } catch (e) {
          this.emitOnError((e as TApiHttpClientError).response);
        } finally {
          this.setIsLoading(false);
        }
      },
      isSearchEnabled(query: string): boolean {
        return query.length >= this.searchMinChars;
      },
      onSelect(item: unknown | null) {
        if (!item) {
          return;
        }

        this.emitOnSelectItem(item);
      },
      clearAutocompleteData() {
        this.setAutocompleteData([]);
      },
      setIsLoading(state: boolean) {
        this.isLoading = state;
      },
      setAutocompleteData(autocompleteData: unknown[]) {
        this.autocompleteData = autocompleteData;
      },
      setSearchValue(searchValue: string) {
        this.searchValue = searchValue;
      },
      emitOnSelectItem(item: unknown) {
        this.$emit('on-select-item', item);
      },
      emitUpdateModelValue(value: string) {
        this.$emit('update:modelValue', value);
      },
      emitOnError(e: TApiHttpClientErrorResponse) {
        this.$emit('on-error', e);
      },
      emitOnFocus(e: FocusEvent) {
        this.$emit('on-focus', e);
      },
      emitOnBlur(e: FocusEvent) {
        this.$emit('on-blur', e);
      },
    },
  });
</script>
